// Rollenmodell des Clubs. Reine Logik ohne Firebase-Import, dadurch direkt
// aus Jest testbar. Backend-Spiegel: functions/shared/membership.js -
// beide Dateien synchron halten, die Server-Validierung prüft gegen genau
// diese Werte.

import { CLUB } from './constants';
import { vehiclesOf } from './vehicles';

/**
 * Die drei Zugehörigkeiten. Bewusst ein eigenes Feld `membership` und nicht
 * das vorhandene `role`: `role` unterscheidet Admin von Nicht-Admin (und
 * hängt an einer Custom Claim), `membership` beschreibt die Zugehörigkeit zum
 * Club. Ein Admin kann Gast sein, ein Crew-Mitglied kein Admin.
 */
export const MEMBERSHIP = {
  GUEST: 'guest',
  MEMBER: 'member',
  CREW: 'crew',
};

/**
 * Aufsteigende Rangfolge. Sie steuert drei Dinge: welche Farbe ein
 * PLZ-Gebiet auf der Karte bekommt, wer einen Termin sehen darf, und welche
 * Stufe bei einer Mischung gewinnt.
 */
export const MEMBERSHIP_ORDER = [MEMBERSHIP.GUEST, MEMBERSHIP.MEMBER, MEMBERSHIP.CREW];

export const ALL_MEMBERSHIPS = MEMBERSHIP_ORDER;

export const MEMBERSHIP_LABELS = {
  guest: 'Gast',
  member: 'Mitglied',
  crew: 'Crew-Mitglied',
};

/** Für Überschriften und Zähler ("3 Gäste"). */
export const MEMBERSHIP_LABELS_PLURAL = {
  guest: 'Gäste',
  member: 'Mitglieder',
  crew: 'Crew',
};

export const MEMBERSHIP_DESCRIPTIONS = {
  guest: 'Autobegeistert, aber (noch) ohne MINI. Willkommen bei allen offenen Treffen.',
  member: 'Fährt einen MINI und gehört fest dazu.',
  crew: 'Ist regelmäßig bei Treffen dabei und packt bei der Orga mit an.',
};

/**
 * Antwort auf die Frage "Fährst du einen MINI?" bei der Anmeldung.
 *
 * Warum überhaupt gefragt wird, statt es am Fahrzeugfeld abzulesen: Das Feld
 * ist Freitext. "Cooper S R53" enthält das Wort MINI nicht, und ein leeres
 * Feld heißt nicht zwingend "kein Auto" - es heißt oft nur "später
 * ausgefüllt". Eine Erkennung darauf läge regelmäßig daneben, und zwar
 * unbemerkt: Der Betroffene sähe nur, dass er als Gast eingestuft wurde.
 */
export const HAS_MINI = {
  YES: 'yes',
  PLANNED: 'planned',
  NO: 'no',
};

export const HAS_MINI_OPTIONS = [
  { value: HAS_MINI.YES, label: 'Ja, ich fahre einen MINI' },
  { value: HAS_MINI.PLANNED, label: 'Noch nicht - aber einer ist geplant' },
  { value: HAS_MINI.NO, label: 'Nein, ich bin einfach autobegeistert' },
];

export const ALL_HAS_MINI = Object.values(HAS_MINI);

/**
 * Bestandsprofile haben kein `membership`-Feld. Sie als Gast zu behandeln
 * wäre die falsche Voreinstellung - es sind die Leute, die vor der Einführung
 * des Modells beigetreten sind, also Mitglieder. Die einmalige Einstufung
 * (migrateMemberships) räumt das auf; bis dahin sieht niemand weniger als
 * vorher.
 */
export function normalizeMembership(value) {
  return MEMBERSHIP_ORDER.includes(value) ? value : MEMBERSHIP.MEMBER;
}

/** 0 = Gast, 1 = Mitglied, 2 = Crew. */
export function membershipRank(value) {
  return MEMBERSHIP_ORDER.indexOf(normalizeMembership(value));
}

/**
 * Die höchste vorkommende Zugehörigkeit einer Gruppe von Leuten.
 * Sind in einem PLZ-Gebiet Gäste, Mitglieder und Crew, gewinnt die Crew.
 */
export function highestMembership(members) {
  let best = null;
  for (const member of members ?? []) {
    const value = normalizeMembership(member?.membership);
    if (best === null || membershipRank(value) > membershipRank(best)) best = value;
  }
  return best;
}

/** Zählt je Zugehörigkeit - für Legende, Gebietsliste und Detailansicht. */
/**
 * Darf dieser Nutzer den Bereich "Die Crew" sehen?
 *
 * Bewusst enger als `CrewRoute` in seiner Standardform: Dort reicht auch
 * eine Orga-Rolle, hier zaehlt ausschliesslich die ZUGEHOERIGKEIT. Ein
 * Organisator kann der Zugehoerigkeit nach Mitglied sein, und fuer den ist
 * der Bereich nicht gedacht (Christians Vorgabe, 20.09.2026).
 *
 * Admins sind drin, weil sie den Club verwalten und sonst eine Seite
 * pflegen muessten, die sie selbst nicht sehen.
 *
 * Eine Anzeige-Regel, keine Sicherheitsgrenze: Der Bereich liest dieselbe
 * Mitgliederliste wie die Uebersicht und zeigt nichts, was ein aktives
 * Mitglied nicht ohnehin lesen darf. Er sortiert nur.
 */
export function maySeeCrewArea(membership, isAdmin = false) {
  if (isAdmin === true) return true;
  return normalizeMembership(membership) === MEMBERSHIP.CREW;
}

export function countByMembership(members) {
  const counts = { guest: 0, member: 0, crew: 0 };
  for (const member of members ?? []) {
    counts[normalizeMembership(member?.membership)] += 1;
  }
  return counts;
}

/** Aus der Anmeldung: Ohne MINI wird man Gast, mit MINI Mitglied. */
export function membershipFromRegistration(hasMini) {
  return hasMini === HAS_MINI.YES ? MEMBERSHIP.MEMBER : MEMBERSHIP.GUEST;
}

/**
 * PLZ-Gebiete der Stammregion (Bonn/Sieg: 51xxx-53xxx und Umgebung).
 * Gespeichert sind nur die ersten drei Stellen - siehe utils/plz.js.
 */
export function isHomeArea(plzArea) {
  return typeof plzArea === 'string' && plzArea.startsWith('5');
}

/**
 * Einmalige Einstufung der Bestandsprofile.
 *
 * Zwei Regeln von Christian, die sich widersprechen können: "PLZ beginnt mit
 * 5 -> Crew" und "kein MINI -> Gast". Entschieden ist: **Gast schlägt PLZ**.
 * Crew ist laut Definition ein aktives *Mitglied*, und Mitglied ist
 * MINI-Besitzer - jemanden ohne MINI zur Crew zu machen widerspräche dem
 * Modell, das hier gerade eingeführt wird.
 *
 * "Kein MINI" heißt für den Bestand: leeres Fahrzeugfeld. Das Feld ist mit
 * "Dein MINI" beschriftet - wer dort etwas eingetragen hat, hat einen. Aus
 * dem Text zu erraten, ob ein Modellname ein MINI ist, wäre die schlechtere
 * Wette: Bei "Cooper S", "R53" oder "JCW" käme jede Heuristik falsch heraus.
 * Deshalb nur "leer oder nicht" - und die Einstufung ist im Admin änderbar.
 */
export function classifyExistingMember(profile) {
  // Ueber vehiclesOf und nicht direkt ueber `carModel`: Seit es mehrere
  // Fahrzeuge gibt, liegt das Modell in der `vehicles`-Map. Wer seine
  // Fahrzeuge gepflegt hat, haette hier sonst "kein MINI" und wuerde zum Gast
  // heruntergestuft - die Funktion laeuft zwar nur ueber Profile ohne
  // `membership`, aber genau solche stillen Fehlklassifizierungen will man
  // nicht als Zeitbombe liegen lassen.
  const hasMini = vehiclesOf(profile).some((v) => (v.model ?? '').trim() !== '');
  if (!hasMini) return MEMBERSHIP.GUEST;
  return isHomeArea(profile?.plzArea) ? MEMBERSHIP.CREW : MEMBERSHIP.MEMBER;
}

// ── Sichtbarkeit von Terminen ────────────────────────────────────────────

/**
 * Wer einen Termin sehen darf. `public` ist keine Zugehörigkeit, sondern die
 * Aussage "steht auf der öffentlichen Webseite, auch ohne Anmeldung".
 */
export const EVENT_AUDIENCE = {
  PUBLIC: 'public',
  GUEST: 'guest',
  MEMBER: 'member',
  CREW: 'crew',
};

/** Aufsteigend nach Exklusivität: öffentlich ist am offensten. */
export const AUDIENCE_ORDER = [
  EVENT_AUDIENCE.PUBLIC,
  EVENT_AUDIENCE.GUEST,
  EVENT_AUDIENCE.MEMBER,
  EVENT_AUDIENCE.CREW,
];

export const AUDIENCE_LABELS = {
  public: 'Öffentlich auf der Webseite',
  guest: 'Gäste',
  member: 'Mitglieder',
  crew: 'Crew',
};

/** Kurzform für die Terminkarte. */
export const AUDIENCE_BADGES = {
  public: 'Öffentlich',
  guest: 'Alle Angemeldeten',
  member: 'Mitglieder & Crew',
  crew: 'Nur Crew',
};

/**
 * Ergänzt die Auswahl des Organisators um alle höheren Gruppen.
 *
 * Entscheidung Christian: gestuft statt frei. Wer "Mitglieder" anhakt, meint
 * Mitglieder UND Crew - ein Crew-Mitglied ist ja ein Mitglied. Ohne diese
 * Ergänzung verpasst die Crew einen Termin, weil ein Häkchen fehlt, und
 * niemand merkt es: Der Termin ist dann einfach nicht da.
 *
 * Gespeichert wird trotzdem die ausgeschriebene Liste und nicht die niedrigste
 * Stufe. Grund ist die Abfrage: Der Client kann dann mit
 * where('audiences','array-contains', eigeneZugehörigkeit) genau die Termine
 * holen, die er sehen darf - eine Abfrage, die Firestore auch mit den Rules
 * durchgehen lässt. Eine Rangfolge-Prüfung ließe sich nicht abfragen.
 */
export function expandAudiences(selected) {
  const picked = new Set(Array.isArray(selected) ? selected : []);
  const isPublic = picked.has(EVENT_AUDIENCE.PUBLIC);

  // Öffentlich heißt: auch jeder Angemeldete. Sonst öffnet die niedrigste
  // angehakte Zugehörigkeit alle darüber.
  const lowest = isPublic ? MEMBERSHIP.GUEST : MEMBERSHIP_ORDER.find((m) => picked.has(m));
  if (!lowest) return isPublic ? [EVENT_AUDIENCE.PUBLIC] : [];

  const groups = MEMBERSHIP_ORDER.slice(membershipRank(lowest));
  return isPublic ? [EVENT_AUDIENCE.PUBLIC, ...groups] : groups;
}

/**
 * Steht das Häkchen für diese Stufe? Alles ab der gewählten Stufe aufwärts
 * ist mit angehakt - das macht die Rangfolge im Formular sichtbar, statt sie
 * nur in einen Hinweistext zu schreiben.
 */
export function audienceChecked(selected, level) {
  return AUDIENCE_ORDER.indexOf(level) >= AUDIENCE_ORDER.indexOf(selected);
}

/**
 * Welche Stufe nach einem Klick gilt.
 *
 * Anhaken heißt: ab hier aufwärts. Abhaken heißt: diese Gruppe soll ihn
 * gerade NICHT sehen - die Grenze rückt also eine Stufe nach oben. Die
 * oberste Stufe (Crew) lässt sich nicht abwählen, sonst gäbe es einen
 * Termin, den niemand sehen darf.
 */
export function nextAudience(level, checked) {
  const index = AUDIENCE_ORDER.indexOf(level);
  if (index < 0) return EVENT_AUDIENCE.PUBLIC;
  if (checked) return level;
  return AUDIENCE_ORDER[Math.min(index + 1, AUDIENCE_ORDER.length - 1)];
}

/** Darf diese Zugehörigkeit den Termin sehen? */
export function canSeeEvent(audiences, membership) {
  const list = Array.isArray(audiences) ? audiences : [];
  return list.includes(normalizeMembership(membership));
}

export function isPublicEvent(audiences) {
  return Array.isArray(audiences) && audiences.includes(EVENT_AUDIENCE.PUBLIC);
}

/**
 * Die offenste Stufe einer Liste - das ist die Aussage, die auf die
 * Terminkarte gehört ("Öffentlich" bzw. "Nur Crew").
 */
export function lowestAudience(audiences) {
  const list = Array.isArray(audiences) ? audiences : [];
  return AUDIENCE_ORDER.find((a) => list.includes(a)) ?? null;
}

/**
 * Legacy-Feld `visibility` aus der Zielgruppenliste ableiten.
 *
 * `audiences` ist die Wahrheit; `visibility` wird nur noch mitgeschrieben,
 * damit ein Rückbau auf die vorherige Fassung möglich bleibt (die fragt
 * where('visibility','==','public') ab und fände sonst gar nichts mehr). Die
 * Ableitung steht bewusst hier und nicht an den Schreibstellen: Zwei Felder,
 * die auseinanderlaufen können, sind genau die Sorte Zustand, die man nicht
 * von Hand pflegt. Ein Eintrag in der ROADMAP erinnert daran, das Feld nach
 * ein paar Wochen ganz zu entfernen.
 */
export function visibilityFromAudiences(audiences) {
  return isPublicEvent(audiences) ? 'public' : 'members';
}

/**
 * Mit welcher Zugehörigkeit die Terminliste abgefragt wird.
 *
 * `null` heißt "ohne Filter, alles sehen" - das dürfen Admins und
 * Organisatoren, weil sie den Clubkalender pflegen (siehe firestore.rules).
 */
export function audienceFilterFor(membership, canSeeAll) {
  return canSeeAll ? null : normalizeMembership(membership);
}

/**
 * Bestandstermine ohne `audiences`-Feld auf die neue Form abbilden.
 *
 * Wird an zwei Stellen gebraucht, die sich nicht absprechen können: bei der
 * einmaligen Umstellung der Daten und in firestore.rules (dort als Ternär
 * nachgebaut). "Nur Mitglieder" hieß bisher "alle angemeldeten aktiven
 * Konten" - das ist nach der Umstellung `guest` und aufwärts, NICHT `member`.
 * Alles andere entzöge Bestandsgästen einen Termin, den sie bisher sahen.
 */
export function audiencesFromLegacyVisibility(visibility) {
  return visibility === 'public'
    ? [EVENT_AUDIENCE.PUBLIC, ...MEMBERSHIP_ORDER]
    : [...MEMBERSHIP_ORDER];
}

/**
 * Die Zielgruppen eines Termins - mit Rückfall auf das alte Feld.
 *
 * Jede Anzeige geht hierüber statt direkt auf `event.audiences`: Zwischen dem
 * Deploy und der einmaligen Umstellung (migrateMemberships) gibt es Termine
 * ohne das neue Feld, und die stünden sonst alle als „Nur Crew" auf der
 * Karte - `lowestAudience([])` findet nichts, `isPublicEvent([])` ist falsch.
 * Dieselbe Übersetzung macht firestore.rules für das Lesen.
 */
export function eventAudiences(event) {
  const list = event?.audiences;
  if (Array.isArray(list) && list.length > 0) return list;
  return audiencesFromLegacyVisibility(event?.visibility);
}

/**
 * Sentinel für „keine Stufe gewählt". Bewusst `__…__`, damit er nie mit
 * einem echten Stufenwert kollidiert.
 */
export const AUDIENCE_ALL = '__all__';

/**
 * Termine je Stufe zählen - nach der offensten Stufe, also genau der
 * Einstufung, die das Abzeichen an jeder Terminzeile zeigt. Kachel und
 * Abzeichen dürfen nicht verschiedene Dinge meinen.
 */
export function countEventsByAudience(events) {
  const counts = { public: 0, guest: 0, member: 0, crew: 0 };
  for (const event of events ?? []) {
    const level = lowestAudience(eventAudiences(event));
    if (level) counts[level] += 1;
  }
  return counts;
}

export function filterEventsByAudience(events, audience) {
  const list = events ?? [];
  if (!audience || audience === AUDIENCE_ALL) return list;
  return list.filter((event) => lowestAudience(eventAudiences(event)) === audience);
}

/**
 * Welche Stufen jemand als Kachel sieht: alle bis zur eigenen. Ein Mitglied
 * bekommt keine Crew-Kachel - die stünde bei ihm immer auf 0, weil er
 * Crew-Termine gar nicht laden darf, und behauptete damit etwas Falsches.
 * Admins und Organisatoren sehen den ganzen Kalender, also alle Stufen.
 */
export function visibleAudienceLevels(membership, canSeeAll = false) {
  if (canSeeAll) return AUDIENCE_ORDER;
  return AUDIENCE_ORDER.slice(0, AUDIENCE_ORDER.indexOf(normalizeMembership(membership)) + 1);
}

// ── "Dabei seit" ─────────────────────────────────────────────────────────

/**
 * Frühestes wählbares Jahr: das Gründungsjahr des Clubs.
 *
 * Abgeleitet und nicht abgeschrieben. In v0.8.10 stand hier kurz eine eigene
 * 2022 neben `CLUB.since = 2024` — weil die beiden Zahlen sich widersprachen
 * und ich keine davon stillschweigend der anderen anpassen wollte. Christian
 * hat den Widerspruch aufgelöst (das Gründungsjahr war falsch), damit fällt
 * die zweite Zahl weg: Wer das Jahr ändert, ändert es an einer Stelle.
 */
export const MEMBER_SINCE_FROM = CLUB.since;

export const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

/** '2024-03' -> { year: '2024', month: '03' }. Leeres oder Unbrauchbares -> leer. */
export function splitMemberSince(value) {
  if (typeof value !== 'string') return { year: '', month: '' };
  const match = value.match(/^(\d{4})(?:-(\d{2}))?$/);
  if (!match) return { year: '', month: '' };
  return { year: match[1], month: match[2] ?? '' };
}

/**
 * Setzt den gespeicherten Wert zusammen.
 *
 * Ein Jahr ohne Monat bleibt zulässig: Wer den Monat nicht mehr weiß, soll
 * nicht raten müssen, nur damit das Feld ein Format erfüllt.
 */
export function joinMemberSince(year, month) {
  if (!year) return '';
  return month ? `${year}-${month}` : String(year);
}

/**
 * Monat und Jahr, seit dem jemand zum Club gehört.
 *
 * Zwei Auswahlfelder statt `<input type="month">`: Das fällt in Firefox auf
 * ein Textfeld zurück, in das man dann "2024-03" tippen müsste. Bei fünf
 * Jahren und zwölf Monaten sind zwei Listen kürzer als jede Erklärung.
 *
 * Erlaubt sind '' (keine Angabe), 'YYYY' und 'YYYY-MM'. Die reine Jahresform
 * ist kein Altlast-Zugeständnis, sondern gewollt — siehe joinMemberSince.
 */
export function isValidMemberSince(value, now = new Date()) {
  if (value === '') return true;
  if (typeof value !== 'string') return false;

  const { year, month } = splitMemberSince(value);
  if (!year) return false;

  const jahr = Number(year);
  if (jahr < MEMBER_SINCE_FROM || jahr > now.getFullYear()) return false;

  if (!month) return true;
  const monat = Number(month);
  if (monat < 1 || monat > 12) return false;

  // Ein Beitritt in der Zukunft ist keine Angabe, sondern ein Vertipper.
  if (jahr === now.getFullYear() && monat > now.getMonth() + 1) return false;

  return true;
}

/** Auswahlliste der Jahre, neueste zuerst. */
export function memberSinceYears(from = MEMBER_SINCE_FROM, currentYear = new Date().getFullYear()) {
  const years = [];
  for (let year = currentYear; year >= from; year -= 1) years.push(String(year));
  return years;
}

/**
 * Auswahlliste der Monate für ein Jahr.
 *
 * Im laufenden Jahr enden sie beim aktuellen Monat — ein Beitritt im
 * kommenden Dezember steht sonst wählbar in der Liste.
 */
export function memberSinceMonths(year, now = new Date()) {
  const bis =
    Number(year) === now.getFullYear() ? now.getMonth() + 1 : MONTH_NAMES.length;
  return MONTH_NAMES.slice(0, bis).map((name, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: name,
  }));
}

/** Anzeigeform: '2024-03' -> 'März 2024', '2024' -> '2024'. */
export function formatMemberSince(value) {
  const { year, month } = splitMemberSince(value);
  if (!year) return '';
  if (!month) return year;
  return `${MONTH_NAMES[Number(month) - 1] ?? month} ${year}`;
}
